import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthSessionService } from './auth-session.service';
import { AuthService } from './auth.service';

let handlingUnauthorized = false;

function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/auth/signin') ||
    url.includes('/auth/verify-otp') ||
    url.includes('/auth/verify-pin') ||
    url.includes('/auth/serial-signup') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/refresh')
  );
}

function isPlatformApiUrl(url: string): boolean {
  return url.includes('/api/') || url.includes(':8090/');
}

/**
 * Logout only on real auth failure (401).
 * HTTP status 0 (ERR_INTERNET_DISCONNECTED / offline / aborted) must not clear the session —
 * work-order offline + outbox depends on staying logged in.
 * Silent /auth/refresh failures must not force logout (keep-alive handles quietly).
 */
function shouldRedirectToLogin(err: HttpErrorResponse, reqUrl: string): boolean {
  if (isAuthEndpoint(reqUrl) || !isPlatformApiUrl(reqUrl)) {
    return false;
  }
  return err.status === 401;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const authSession = inject(AuthSessionService);
  const ngZone = inject(NgZone);
  const skipAuthHeader = isAuthEndpoint(req.url);

  const token = auth.getAuthToken();

  let headers = req.headers;
  if (token && !skipAuthHeader) {
    headers = headers.set('X-Auth-Token', token);
  }

  const user = auth.getStoredUser();
  if (user?.organizationId) {
    headers = headers.set('X-Organization-Id', String(user.organizationId));
  }

  return next(req.clone({ headers, withCredentials: true })).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && shouldRedirectToLogin(err, req.url)) {
        if (!handlingUnauthorized) {
          handlingUnauthorized = true;
          ngZone.run(() => authSession.signOutAndGoToLogin());
          setTimeout(() => {
            handlingUnauthorized = false;
          }, 1000);
        }
      }
      return throwError(() => err);
    }),
  );
};
